import { Input, Modal, Skeleton } from "antd";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { AiOutlineDownload, AiOutlineEdit, AiOutlineSearch } from "react-icons/ai";
import { BsFileText, BsPlusLg, BsTags } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { loadAllTicketCategory } from "../../../../../redux/rtk/features/crm/ticketCategory/ticketCategorySlice";
import getPermissions from "../../../../../utils/getPermissions";
import UserPrivateComponent from "../../../../PrivateRoutes/UserPrivateComponent";
import CreateCrmTicketCategoryForm from "./CreateCrmTicketCategoryForm";

const categoryAccents = [
	"from-[#023f88] to-[#0b5cae]",
	"from-sky-500 to-blue-600",
	"from-indigo-500 to-violet-600",
	"from-amber-500 to-orange-500",
	"from-rose-500 to-pink-600",
];

const StatCard = ({ icon: Icon, label, value, accent }) => (
	<div className='flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70'>
		<div
			className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-md`}>
			<Icon className='text-2xl' />
		</div>
		<div>
			<p className='text-3xl font-extrabold leading-none text-slate-900'>{value}</p>
			<p className='mt-1 text-sm font-medium text-slate-600'>{label}</p>
		</div>
	</div>
);

const CategoryCard = ({ category, index, canEdit, onEdit }) => {
	const postCount = category._count?.ticket;

	return (
		<div className='group flex flex-col rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl'>
			<div className='flex items-start justify-between'>
				<div
					className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${
						categoryAccents[index % categoryAccents.length]
					} text-white shadow-md transition-transform duration-300 group-hover:scale-110`}>
					<BsTags className='text-2xl' />
				</div>
				{postCount !== undefined && (
					<span className='inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-[#023f88] ring-1 ring-blue-200'>
						<BsFileText />
						{postCount} {postCount === 1 ? "post" : "posts"}
					</span>
				)}
			</div>

			<h3 className='mt-5 text-lg font-bold text-slate-900'>
				{category.ticketCategoryName}
			</h3>
			<div className='mt-1 space-y-0.5 text-sm text-slate-600'>
				<p>Created {moment(category.createdAt).format("DD MMM YYYY")}</p>
				<p>Updated {moment(category.updatedAt).fromNow()}</p>
			</div>

			{canEdit && (
				<button
					type='button'
					onClick={() => onEdit(category)}
					className='mt-5 inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#023f88] px-3 py-2 text-sm font-semibold text-[#023f88] transition hover:bg-[#023f88] hover:text-white'>
					<AiOutlineEdit />
					Edit category
				</button>
			)}
		</div>
	);
};

export default function CrmTicketCategory() {
	const dispatch = useDispatch();
	const { list, loading } = useSelector((state) => state.ticketCategory);
	const [search, setSearch] = useState("");
	const [modal, setModal] = useState(null); // { category } — category is undefined when creating
	const canEdit = (getPermissions() || []).includes("update-ticketCategory");

	useEffect(() => {
		dispatch(loadAllTicketCategory());
	}, [dispatch]);

	const categories = useMemo(() => list || [], [list]);
	const filteredCategories = useMemo(() => {
		const term = search.trim().toLowerCase();
		return term
			? categories.filter((c) => c.ticketCategoryName?.toLowerCase().includes(term))
			: categories;
	}, [categories, search]);

	const hasCounts = categories.some((c) => c._count);
	const totalPosts = categories.reduce((sum, c) => sum + (c._count?.ticket || 0), 0);
	const mostUsed = hasCounts
		? categories.reduce(
				(best, c) => ((c._count?.ticket || 0) > (best?._count?.ticket || 0) ? c : best),
				null
		  )
		: null;

	return (
		<div className='max-w-7xl mx-auto py-6 md:py-8'>
			{/* Header */}
			<div className='relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#011f44] via-[#023f88] to-[#0b5cae] px-6 py-8 md:px-10 text-white shadow-xl'>
				<div className='absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10 blur-2xl' />
				<div className='absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-sky-300/20 blur-2xl' />

				<div className='relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between'>
					<div>
						<span className='inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium uppercase tracking-wide backdrop-blur-sm'>
							<BsTags />
							Content
						</span>
						<h1 className='mt-4 text-3xl md:text-4xl font-extrabold tracking-tight text-white'>
							Post Categories
						</h1>
						<p className='mt-2 max-w-xl text-blue-50/90'>
							Set up the categories used to classify posts across the feed.
						</p>
					</div>

					<div className='flex flex-wrap gap-3'>
						<UserPrivateComponent permission={"create-ticketCategory"}>
							<button
								type='button'
								onClick={() => setModal({})}
								className='inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#023f88] shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl'>
								<BsPlusLg />
								Create Category
							</button>
						</UserPrivateComponent>
						{categories.length > 0 && (
							<CSVLink
								data={categories.map(({ _count, ...c }) => ({
									...c,
									posts: _count?.ticket ?? "",
								}))}
								filename='post-categories'
								className='inline-flex items-center gap-2 rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur-sm transition hover:bg-white/25 hover:text-white'>
								<AiOutlineDownload className='text-lg' />
								Download CSV
							</CSVLink>
						)}
					</div>
				</div>
			</div>

			{/* Stats */}
			<div className={`mt-6 grid gap-4 ${hasCounts ? "sm:grid-cols-3" : "sm:grid-cols-1"}`}>
				<StatCard
					icon={BsTags}
					label='Total categories'
					value={categories.length}
					accent='from-[#023f88] to-[#0b5cae]'
				/>
				{hasCounts && (
					<>
						<StatCard
							icon={BsFileText}
							label='Posts categorised'
							value={totalPosts}
							accent='from-green-500 to-green-600'
						/>
						<StatCard
							icon={BsTags}
							label={mostUsed?._count?.ticket ? "Most used category" : "Most used category (none yet)"}
							value={
								<span className='block max-w-[12rem] truncate text-xl'>
									{mostUsed?._count?.ticket ? mostUsed.ticketCategoryName : "—"}
								</span>
							}
							accent='from-amber-500 to-orange-500'
						/>
					</>
				)}
			</div>

			{/* Search */}
			<div className='mt-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between'>
				<div>
					<h2 className='text-xl md:text-2xl font-bold text-slate-900'>All categories</h2>
					<p className='text-sm text-slate-600'>
						{filteredCategories.length} of {categories.length} categories
					</p>
				</div>
				<Input
					allowClear
					size='large'
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					prefix={<AiOutlineSearch className='text-slate-400' />}
					placeholder='Search categories…'
					className='rounded-xl sm:max-w-xs'
				/>
			</div>

			{/* Categories */}
			{loading && !categories.length ? (
				<div className='mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3'>
					{[0, 1, 2].map((i) => (
						<div key={i} className='rounded-2xl bg-white p-6 ring-1 ring-slate-200/70'>
							<Skeleton active />
						</div>
					))}
				</div>
			) : filteredCategories.length ? (
				<div className='mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3'>
					{filteredCategories.map((category, i) => (
						<CategoryCard
							key={category.id}
							category={category}
							index={i}
							canEdit={canEdit}
							onEdit={(c) => setModal({ category: c })}
						/>
					))}
				</div>
			) : (
				<div className='mt-5 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center'>
					<BsTags className='mx-auto text-4xl text-slate-300' />
					<p className='mt-3 font-semibold text-slate-700'>No categories found</p>
					<p className='mt-1 text-sm text-slate-600'>
						Try a different search or create a new category.
					</p>
				</div>
			)}

			<Modal
				open={!!modal}
				onCancel={() => setModal(null)}
				footer={null}
				centered
				destroyOnClose
				title={
					<div className='flex items-center gap-3'>
						<div className='flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#023f88] to-[#0b5cae] text-white'>
							{modal?.category ? <AiOutlineEdit /> : <BsPlusLg />}
						</div>
						<div>
							<p className='text-lg font-bold text-slate-900'>
								{modal?.category ? "Edit post category" : "Create post category"}
							</p>
							<p className='text-sm font-normal text-slate-600'>
								{modal?.category
									? modal.category.ticketCategoryName
									: "Posts can be filed under it right away."}
							</p>
						</div>
					</div>
				}>
				<div className='pt-2'>
					<CreateCrmTicketCategoryForm
						onClose={() => setModal(null)}
						edit={
							modal?.category
								? {
										id: modal.category.id,
										values: { ticketCategoryName: modal.category.ticketCategoryName },
								  }
								: undefined
						}
					/>
				</div>
			</Modal>
		</div>
	);
}
